import { IonButton, IonContent, IonIcon, IonPage, IonSpinner, IonTextarea, useIonToast } from "@ionic/react";
import { arrowBackOutline, attachOutline, closeCircleOutline } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Header from "src/components/Header";
import SemaforoChip from "src/components/SemaforoSla";
import { campoEstilo, Etiqueta, fmtFecha } from "src/components/SolicitudCapacitacionCampos";
import SstAPI, { AdjuntoLevantamiento, LevantamientoDetalle } from "src/services/sst";

const VOLVER = "/sst/acciones-correctivas";
const MIME_ACEPTADOS = "image/*,application/pdf,.doc,.docx,.xls,.xlsx";
const TAMANO_MAX = 10 * 1024 * 1024;

const soloLectura: React.CSSProperties = {
  background: "#f1f1f1",
  border: "1px solid #ddd",
  borderRadius: 4,
  padding: "8px 10px",
  fontSize: 14,
  minHeight: 20,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const Dato: React.FC<{ etiqueta: string; valor?: string | null }> = ({ etiqueta, valor }) => (
  <div style={campoEstilo}>
    <Etiqueta texto={etiqueta} />
    <div style={soloLectura}>{valor || "-"}</div>
  </div>
);

const fmtFechaHora = (f?: string | null) => (f ? `${fmtFecha(f)} ${f.substring(11, 16)}` : "");

const Adjuntos: React.FC<{ adjuntos: AdjuntoLevantamiento[] }> = ({ adjuntos }) =>
  !adjuntos.length ? null : (
    <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 14 }}>
      {adjuntos.map((a) => (
        <li key={a.id}>
          {a.url_firmada ? (
            <a href={a.url_firmada} target="_blank" rel="noopener noreferrer">
              {a.nombre_archivo}
            </a>
          ) : (
            a.nombre_archivo
          )}
        </li>
      ))}
    </ul>
  );

/**
 * Detalle de un levantamiento de condición insegura asignado al usuario
 * (/sst/acciones-correctivas/:id). Muestra todo lo cargado por SHO y, si está
 * pendiente o devuelto, el formulario de acciones correctivas con adjunto
 * opcional. Al enviar vuelve a quien inició el caso para el cierre en EMSYS.
 */
const AccionCorrectivaDetalle: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const inputRef = useRef<HTMLInputElement>(null);

  const [lev, setLev] = useState<LevantamientoDetalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [acciones, setAcciones] = useState("");
  const [archivo, setArchivo] = useState<File | null>(null);

  useEffect(() => {
    const levId = Number(id);
    if (!levId) return;
    setCargando(true);
    setAcciones("");
    setArchivo(null);
    SstAPI.detalle(levId)
      .then((r) => setLev(r.data.data))
      .catch((err) =>
        present({
          message: err?.response?.data?.error || "No se pudo obtener el levantamiento",
          duration: 4000,
          color: "danger",
        })
      )
      .finally(() => setCargando(false));
  }, [id, present]);

  const enviar = async () => {
    if (!lev) return;
    if (!acciones.trim()) {
      present({ message: "Describí las acciones correctivas ejecutadas", duration: 3000, color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const r = await SstAPI.responder(lev.id, acciones.trim(), archivo);
      present({ message: r.data.data?.info || "Acciones correctivas enviadas", duration: 3000, color: "success" });
      // El boton queda deshabilitado hasta salir de la pantalla: evita un
      // segundo envio y actualizar el estado de una pagina ya desmontada.
      history.replace(VOLVER);
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudieron enviar las acciones correctivas",
        duration: 4000,
        color: "danger",
      });
      setGuardando(false);
    }
  };

  const informes = lev?.adjuntos.filter((a) => a.tipo === "INFORME") || [];
  const archivosAccion = lev?.adjuntos.filter((a) => a.tipo === "ACCION") || [];
  const devoluciones = lev?.historial.filter((h) => h.accion === "DEVOLVER") || [];

  return (
    <IonPage>
      <Header titulo={`Levantamiento N° ${id}`} botonIzq={{ icono: arrowBackOutline, url: VOLVER }} />
      <IonContent id="sst-accion-correctiva">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          {cargando ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          ) : !lev ? (
            <p style={{ color: "#777" }}>El levantamiento no existe o no está asignado a vos.</p>
          ) : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
                <SemaforoChip semaforo={lev.semaforo} />
                <span style={{ fontSize: 13, color: "#555" }}>
                  {lev.estado_label} · plazo {lev.sla_dias} días · vence {fmtFecha(lev.fecha_limite)}
                </span>
              </div>

              {lev.estado === "DEVUELTO" && devoluciones.length > 0 && (
                <div
                  style={{
                    background: "#fbe3e5",
                    borderLeft: "4px solid #b21f2d",
                    borderRadius: 6,
                    padding: "10px 12px",
                    marginBottom: 16,
                    fontSize: 14,
                  }}
                >
                  <strong>Devuelto por quien inició el caso</strong>
                  <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{lev.comentario_cierre}</div>
                  <small style={{ color: "#777" }}>{fmtFechaHora(devoluciones[devoluciones.length - 1].fecha)}</small>
                </div>
              )}

              <Dato etiqueta="Condición Insegura" valor={lev.condicion_label} />
              {lev.condicion === "LUGAR" ? (
                <>
                  <Dato etiqueta="Nombre del PS" valor={lev.ps_nombre} />
                  <Dato etiqueta="Código del PS" valor={lev.ps_codigo} />
                  <Dato etiqueta="Sector" valor={lev.sector} />
                  <Dato etiqueta="Fiscal Responsable" valor={lev.fiscal_nombre} />
                </>
              ) : (
                <>
                  <Dato etiqueta="Legajo" valor={lev.colaborador_legajo} />
                  <Dato etiqueta="Colaborador" valor={lev.colaborador_nombre} />
                  <Dato etiqueta="Cédula" valor={lev.colaborador_cedula} />
                  <Dato etiqueta="Teléfono" valor={lev.colaborador_telefono} />
                  <Dato etiqueta="Fecha de Ingreso" valor={fmtFecha(lev.colaborador_fecha_ingreso)} />
                </>
              )}
              <Dato etiqueta="Unidad de Negocio Afectada" valor={lev.unidad_negocio} />
              <Dato etiqueta="Área Afectada" valor={lev.area} />
              <Dato etiqueta="Fecha de Inspección" valor={fmtFecha(lev.fecha_inspeccion)} />
              <Dato etiqueta="Anomalía Detectada" valor={lev.anomalia} />
              <Dato etiqueta="Descripción de la Anomalía Detectada por el Personal de SHO" valor={lev.descripcion} />
              {informes.length > 0 && (
                <div style={campoEstilo}>
                  <Etiqueta texto="Informe adjunto" />
                  <Adjuntos adjuntos={informes} />
                </div>
              )}

              {lev.puede_responder ? (
                <>
                  <div style={campoEstilo}>
                    <Etiqueta texto="Acciones Correctivas Ejecutadas" obligatorio />
                    <IonTextarea
                      value={acciones}
                      onIonChange={(e) => setAcciones(e.detail.value || "")}
                      autoGrow
                      rows={4}
                      maxlength={4000}
                      disabled={guardando}
                      style={{ border: "1px solid #ccc", borderRadius: 4, padding: "0 8px" }}
                    />
                  </div>

                  <div style={campoEstilo}>
                    <Etiqueta texto="Adjuntar Archivo de la Acción Ejecutada" />
                    <input
                      ref={inputRef}
                      type="file"
                      accept={MIME_ACEPTADOS}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const elegido = e.target.files?.[0] || null;
                        if (elegido && elegido.size > TAMANO_MAX) {
                          present({ message: "El archivo supera los 10 MB", duration: 3000, color: "warning" });
                        } else {
                          setArchivo(elegido);
                        }
                        if (inputRef.current) inputRef.current.value = "";
                      }}
                    />
                    {archivo ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1, fontSize: 14, wordBreak: "break-all" }}>{archivo.name}</span>
                        <IonButton fill="clear" size="small" color="danger" onClick={() => setArchivo(null)}>
                          <IonIcon icon={closeCircleOutline} slot="icon-only" />
                        </IonButton>
                      </div>
                    ) : (
                      <IonButton
                        size="small"
                        fill="outline"
                        disabled={guardando}
                        onClick={() => inputRef.current?.click()}
                      >
                        <IonIcon icon={attachOutline} slot="start" />
                        Adjuntar desde tu dispositivo
                      </IonButton>
                    )}
                    <small style={{ display: "block", color: "#777" }}>Opcional. Imagen, PDF, Word o Excel.</small>
                  </div>

                  <IonButton expand="block" disabled={guardando} onClick={enviar}>
                    {guardando ? <IonSpinner name="crescent" /> : "Enviar acciones correctivas"}
                  </IonButton>
                </>
              ) : (
                <>
                  <Dato etiqueta="Acciones Correctivas Ejecutadas" valor={lev.acciones_correctivas} />
                  {archivosAccion.length > 0 && (
                    <div style={campoEstilo}>
                      <Etiqueta texto="Archivos de la acción ejecutada" />
                      <Adjuntos adjuntos={archivosAccion} />
                    </div>
                  )}
                  <p style={{ fontSize: 13, color: "#555" }}>
                    {lev.estado === "CERRADO"
                      ? `Cerrado el ${fmtFechaHora(lev.cerrado_en)}. ${lev.comentario_cierre || ""}`
                      : "Enviado. Quien inició el caso lo está revisando para el cierre."}
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AccionCorrectivaDetalle;
